import { LucideIcon } from 'lucide-react';
import Dot from '@/components/ui/Dot.tsx';
import { cn } from '@/lib/utils.ts';
import { STATUS_TONE_COLORS, StatusTone } from '@/components/ui/statusTone.ts';

interface StatusDotProps {
  label: string;
  /**
   * Zwykłe wywołanie podaje ton i nie musi wiedzieć nic o barwach. Rejestr
   * enumów tabeli niesie gotowe heksy, więc może je nadpisać poniżej.
   */
  tone?: StatusTone;
  /** Barwa kropki. `null` znaczy „bez znacznika": kategoria, nie stan. */
  color?: string | null;
  /**
   * Barwa ikony — osobna od kropki, bo kółko 8 px i ikona 14 px potrzebują
   * innej jasności; bez niej ikona idzie kolorem tekstu wiersza.
   */
  iconColor?: string | null;
  /**
   * Barwa etykiety stanu wymagającego reakcji („Zaległe”, „Anulowane”).
   * Bez niej etykieta idzie zwykłym tekstem — spokojne stany nie mają
   * krzyczeć głośniej niż nazwy obok.
   */
  attentionColor?: string | null;
  /** Ikona zamiast kropki. */
  icon?: LucideIcon;
  /** `false` dla wartości pokazywanych samą ikoną. */
  showLabel?: boolean;
  className?: string;
}

/**
 * Status jako kropka z etykietą — jeden wzór dla wszystkich statusów
 * w aplikacji, zamiast wersalikowej odznaki na kolorowym tle. Przy liście
 * kilkunastu pozycji kolumna statusu przestaje być pasem plam.
 *
 * Pigułka zostaje dla tego, co statusem nie jest: roli, waluty, liczby,
 * kategorii — tam kropka niosłaby znaczenie, którego nie ma.
 */
export const StatusDot = ({
  label,
  tone,
  color,
  iconColor,
  attentionColor,
  icon: Icon,
  showLabel = true,
  className,
}: StatusDotProps) => {
  const toneColors = tone ? STATUS_TONE_COLORS[tone] : undefined;
  const dotColor = color !== undefined ? color : toneColors?.dot;
  const markIconColor = iconColor !== undefined ? iconColor : toneColors?.text;
  const labelColor =
    attentionColor !== undefined
      ? attentionColor
      : toneColors?.attention
        ? toneColors.text
        : null;

  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center gap-2 align-middle',
        className,
      )}
    >
      {Icon ? (
        <Icon
          className="size-3.5 shrink-0"
          style={{ color: markIconColor ?? undefined }}
          aria-hidden
        />
      ) : (
        dotColor && <Dot size="md" style={{ backgroundColor: dotColor }} />
      )}
      {showLabel ? (
        <span
          className={cn('truncate', labelColor && 'font-semibold')}
          style={labelColor ? { color: labelColor } : undefined}
        >
          {label}
        </span>
      ) : (
        /* Ikona jest `aria-hidden`, więc bez tego komórka pokazywana samą
           ikoną nie ma dla czytnika ekranu żadnej treści. */
        <span className="sr-only">{label}</span>
      )}
    </span>
  );
};

export default StatusDot;
